/**
 * TanStack Query hooks for the super-admin account-requests queue.
 *
 * Exposes one query (list pending requests) and two mutations
 * (approve, deny). Every mutation invalidates BOTH the
 * `["admin", "account-requests"]` and `["admin", "users"]` caches
 * because a state change on one side of the flow — e.g., approving
 * a pending request — affects the other side (the user is no longer
 * pending and their row in the users list now shows status=active).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { AdminUser } from "@/features/admin/useUsers";
import { ADMIN_USERS_QUERY_KEY } from "@/features/admin/useUsers";

export type AccountRequestStatus = "pending" | "approved" | "denied";

export interface AccountRequestRow {
  id: string;
  user_id: string;
  user_email: string;
  user_display_name: string;
  requested_at: string; // ISO datetime
  status: AccountRequestStatus;
  reviewed_at: string | null;
  notes: string | null;
}

const ADMIN_ACCOUNT_REQUESTS_QUERY_KEY = ["admin", "account-requests"] as const;

export function usePendingAccountRequests() {
  return useQuery<AccountRequestRow[]>({
    queryKey: ADMIN_ACCOUNT_REQUESTS_QUERY_KEY,
    queryFn: () => apiFetch<AccountRequestRow[]>("/api/admin/account-requests"),
  });
}

export function useApproveAccountRequest() {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, Error, { requestId: string }>({
    mutationFn: ({ requestId }) =>
      apiFetch<AdminUser>(`/api/admin/account-requests/${requestId}/approve`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_ACCOUNT_REQUESTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

export function useDenyAccountRequest() {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, Error, { requestId: string; reason?: string }>({
    mutationFn: ({ requestId, reason }) =>
      apiFetch<AdminUser>(`/api/admin/account-requests/${requestId}/deny`, {
        method: "POST",
        body: reason ? { reason } : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_ACCOUNT_REQUESTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

export { ADMIN_ACCOUNT_REQUESTS_QUERY_KEY };
